import {
	Button,
	DataTable,
	type DataTableColumn,
	EmptyState,
	Input,
	PageHeader,
	Stack,
	Text,
	Toolbar,
} from "@app/ui"
import { Tag } from "@app/ui/next"
import { useQuery } from "@tanstack/react-query"
import { Link, createRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { type LotSummary, exportLotsCsv, listLots, lotsQueryKeys } from "../../api/lots"
import ExportCsvButton from "../../components/ExportCsvButton"
import { HttpError } from "../../lib/http"
import { useScope } from "../../scope/scope-context"
import { Route as RootRoute } from "../__root"

/**
 * Lots tenant-wide list -- `/lots`.
 *
 * Mirrors `/shipments`: PageHeader + Toolbar (count + search) + DataTable
 * with the canonical five-state scaffold (loading skeleton rows, inline
 * error, borderless empty, empty-with-query, list). A search input re-keys
 * the react-query cache per term so navigation back restores results.
 */
export const Route = createRoute({
	getParentRoute: () => RootRoute,
	path: "/lots",
	component: LotsIndexPage,
})

const COMPANY_LINE =
	"Latest 100 lots for your tenant, newest first. Search by NSB number or lot ID."

export function LotsIndexPage() {
	const navigate = useNavigate()
	const { params: scope } = useScope()
	const [q, setQ] = useState("")
	const trimmedQ = q.trim()
	const query = useQuery({
		queryKey: lotsQueryKeys.list(trimmedQ, scope),
		queryFn: ({ signal }) =>
			listLots({ ...(trimmedQ.length > 0 ? { q: trimmedQ } : {}), scope, signal }),
	})

	const rows = query.data ?? []
	const countLabel = query.isSuccess
		? `${rows.length} ${rows.length === 1 ? "lot" : "lots"}`
		: "Lots"

	const columns: DataTableColumn<LotSummary>[] = [
		{
			key: "ref",
			header: "NSB number",
			mono: true,
			cell: (lot) => (
				// Native link inside the row: preserves cmd/middle-click + prefetch +
				// keyboard focus while the whole row also navigates via onRowClick.
				<Link to="/lots/$lotId" params={{ lotId: lot.id }} onClick={(e) => e.stopPropagation()}>
					{lot.nsbNumber}
				</Link>
			),
		},
		// Show the vertical NAME, never the UUID — `verticalName` may be null.
		// Categorical metadata → Tag (neutral; name carries the category, so no
		// vertical-specific colour). Null falls back to the em-dash.
		{
			key: "vertical",
			header: "Vertical",
			cell: (lot) => (lot.verticalName ? <Tag>{lot.verticalName}</Tag> : "—"),
		},
		{ key: "origin", header: "Origin", cell: (lot) => lot.originCountry },
		{
			key: "quantity",
			header: "Quantity",
			mono: true,
			align: "end",
			cell: (lot) => `${lot.quantity} ${lot.unit}`,
		},
		{
			key: "status",
			header: "Status",
			align: "end",
			// Assigned/unassigned is a category, not a logistics status — Tag, not
			// StatusPill/StatusBadge. harbor = assigned, neutral = unassigned.
			cell: (lot) =>
				lot.shipmentId !== null ? (
					<Tag color="harbor">Assigned</Tag>
				) : (
					<Tag color="neutral">Unassigned</Tag>
				),
		},
	]

	return (
		<Stack gap="5" align="stretch">
			<PageHeader
				title="Lots"
				subtitle={COMPANY_LINE}
				actions={
					<ExportCsvButton
						base="lots"
						scope={scope}
						disabled={rows.length === 0}
						fetchCsv={() =>
							exportLotsCsv({ ...(trimmedQ.length > 0 ? { q: trimmedQ } : {}), scope })
						}
					/>
				}
			/>

			<Toolbar lead={<Text variant="label">{countLabel}</Text>}>
				<Input
					type="search"
					inputSize="sm"
					placeholder="Search NSB number or lot id…"
					value={q}
					onChange={(e) => setQ(e.target.value)}
				/>
				{q.length > 0 ? (
					<Button variant="secondary" size="sm" onClick={() => setQ("")}>
						Clear
					</Button>
				) : null}
			</Toolbar>

			{query.isError ? (
				<ErrorState error={query.error} onRetry={() => query.refetch()} />
			) : (
				<DataTable
					columns={columns}
					rows={rows}
					getRowKey={(lot) => lot.id}
					isLoading={query.isPending}
					onRowClick={(lot) => navigate({ to: "/lots/$lotId", params: { lotId: lot.id } })}
					empty={<EmptyStateBody hasQuery={trimmedQ.length > 0} />}
				/>
			)}
		</Stack>
	)
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const message =
		error instanceof HttpError
			? `HTTP ${error.status}`
			: error instanceof Error
				? error.message
				: "Unknown error"
	return (
		<Stack gap="3" align="start">
			<Text variant="heading-sm" tone="danger">
				Couldn't load lots
			</Text>
			<Text variant="body-sm" tone="muted">
				{message}
			</Text>
			<Button variant="secondary" size="sm" onClick={onRetry}>
				Retry
			</Button>
		</Stack>
	)
}

function EmptyStateBody({ hasQuery }: { hasQuery: boolean }) {
	return (
		<EmptyState
			title={hasQuery ? "No matches" : "No lots yet"}
			description={
				hasQuery
					? "Try a different NSB number or lot ID."
					: "Lots created in the engine will appear here."
			}
		/>
	)
}
